import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import {
  ArrowLeftIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  DocumentArrowDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  ShoppingCartIcon,
  TruckIcon,
  InformationCircleIcon,
  TagIcon,
  ListBulletIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const WarehouseInventoryPage = () => {
  const navigate = useNavigate();
  const { warehouseId } = useParams();
  const { warehouses, products, sales, purchases, stockItems, suppliers, getProducts } = useAppData();
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Find the current warehouse
  const warehouse = useMemo(() => {
    return warehouses.find(w => w.id.toString() === warehouseId?.toString());
  }, [warehouses, warehouseId]);

  // Get all product categories
  const categories = useMemo(() => {
    const allCategories = new Set();
    if (products && products.length > 0) {
      products.forEach(product => {
        if (product.category) {
          allCategories.add(product.category);
        }
      });
    }
    return Array.from(allCategories).sort();
  }, [products]);

  // Function to determine if a product is in this warehouse
  const isProductInWarehouse = useCallback((product) => {
    // Convert warehouseId to string for consistent comparison
    const warehouseIdStr = String(warehouseId);
    
    // Check if product's primary warehouseId matches (with type conversion)
    if (product.warehouseId && String(product.warehouseId) === warehouseIdStr) {
      console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via primary warehouseId`);
      return true;
    }
    
    // Check if product's warehouseIds array includes this warehouse (with type conversion)
    if (product.warehouseIds && Array.isArray(product.warehouseIds)) {
      const hasWarehouse = product.warehouseIds.some(wId => String(wId) === warehouseIdStr);
      if (hasWarehouse) {
        console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via warehouseIds array`);
        console.log(`  warehouseIds: ${product.warehouseIds.join(', ')} (types: ${product.warehouseIds.map(id => typeof id).join(', ')})`);
      return true;
      }
    }
    
    // Check if product has an entry in warehouseStock for this warehouse
    // IMPORTANT: We check if the entry EXISTS, not just if it's > 0
    // This ensures products with 0 stock are still shown in the warehouse
    if (product.warehouseStock) {
      // CRITICAL FIX: Check for the warehouse using multiple key formats
      // First try string key
      if (product.warehouseStock[warehouseIdStr] !== undefined) {
        console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via warehouseStock with quantity ${product.warehouseStock[warehouseIdStr]} (string key)`);
        return true;
      }
      
      // Then try number key
      const warehouseIdNum = parseInt(warehouseIdStr);
      if (product.warehouseStock[warehouseIdNum] !== undefined) {
        console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via warehouseStock with quantity ${product.warehouseStock[warehouseIdNum]} (number key)`);
        return true;
      }
      
      // Try a manual search through all keys
      const matchingKey = Object.keys(product.warehouseStock).find(key => 
        String(key) === warehouseIdStr || 
        (parseInt(key) === parseInt(warehouseIdStr) && !isNaN(parseInt(key)))
      );
      
      if (matchingKey) {
        console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via warehouseStock with quantity ${product.warehouseStock[matchingKey]} (matched key: ${matchingKey})`);
        return true;
      }
      
      console.log(`  warehouseStock keys: ${Object.keys(product.warehouseStock).join(', ')} (types: ${Object.keys(product.warehouseStock).map(k => typeof k).join(', ')})`);
    }
    
    // Check for purchases that added this product to this warehouse
    const relevantPurchases = purchases.filter(purchase => 
      String(purchase.warehouseId) === warehouseIdStr && 
      purchase.items && 
      purchase.items.some(item => item.id === product.id)
    );
    
    if (relevantPurchases.length > 0) {
      console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via purchases`);
      console.log(`  Found in ${relevantPurchases.length} purchases`);
      return true;
    }
    
    // Check stock movements for products added to this warehouse
    const relevantMovements = stockItems.filter(item => 
      (String(item.warehouse) === warehouseIdStr || 
       String(item.location) === warehouseIdStr || 
       String(item.sourceWarehouse) === warehouseIdStr || 
       String(item.destinationWarehouse) === warehouseIdStr) && 
      item.productId === product.id &&
      (item.type === 'entrée' || 
       (item.type === 'transfert' && String(item.destinationWarehouse) === warehouseIdStr))
    );
    
    if (relevantMovements.length > 0) {
      console.log(`Product ${product.id} (${product.name}) is in warehouse ${warehouseIdStr} via stock movements`);
      console.log(`  Found in ${relevantMovements.length} stock movements`);
      return true;
    }
    
    return false;
  }, [warehouseId, purchases, stockItems, products]);

  // Calculate product quantity in this warehouse
  const getProductQuantityInWarehouse = useCallback((productId) => {
    // Convert warehouseId to string for consistent comparison
    const warehouseIdStr = String(warehouseId);
    
    // STEP 1: Check warehouseStock as the primary source of truth
    const product = products.find(p => p.id === productId);
    if (product && product.warehouseStock && typeof product.warehouseStock === 'object') {
      // Try string key first (preferred format)
      const warehouseStockStr = product.warehouseStock[warehouseIdStr];
      if (warehouseStockStr !== undefined) {
        console.log(`Using warehouseStock for ${product.name} in warehouse ${warehouseIdStr}: ${warehouseStockStr}`);
        return parseInt(warehouseStockStr) || 0;
      }
      
      // Try number key as fallback
      const warehouseIdNum = parseInt(warehouseIdStr);
      const warehouseStockNum = product.warehouseStock[warehouseIdNum];
      if (warehouseStockNum !== undefined) {
        console.log(`Using warehouseStock for ${product.name} in warehouse ${warehouseIdStr} (number key): ${warehouseStockNum}`);
        return parseInt(warehouseStockNum) || 0;
      }
      
      // Try a manual search through all keys as last resort
      const matchingKey = Object.keys(product.warehouseStock).find(key => 
        String(key) === warehouseIdStr || 
        (parseInt(key) === parseInt(warehouseIdStr) && !isNaN(parseInt(key)))
      );
      
      if (matchingKey) {
        const warehouseStock = product.warehouseStock[matchingKey];
        console.log(`Using warehouseStock for ${product.name} in warehouse ${warehouseIdStr} (matched key ${matchingKey}): ${warehouseStock}`);
        return parseInt(warehouseStock) || 0;
      }
    }
    
    // STEP 2: Fall back to calculating from movements if warehouseStock isn't available
    console.log(`No warehouseStock data for product ${productId} in warehouse ${warehouseIdStr}. Calculating from movements...`);
    let quantity = 0;
    
    // Process movements to calculate quantity
    stockItems.forEach(item => {
      // STEP 2.1: Skip movements from forms to prevent double-counting
      if (item.fromPurchase || item.fromSale || item.fromEntryForm || item.fromExitForm) {
        console.log(`Skipping movement with form flag: ${item.id}`);
        return;
      }
      
      // STEP 2.2: Handle transfer movements differently
      if (item.type === 'transfert') {
        // Check if this product is involved in the transfer
        const isProductInTransfer = (
          item.productId === productId || 
          (item.items && item.items.some(i => i.id === productId))
        );
        
        if (!isProductInTransfer) return;
        
        // Get quantity for this specific product
        let transferQty = 0;
        if (item.productId === productId) {
          transferQty = parseInt(item.quantity) || 0;
        } else if (item.items) {
          const matchingItem = item.items.find(i => i.id === productId);
        if (matchingItem) {
            transferQty = parseInt(matchingItem.quantity) || 0;
          }
        }
        
        if (transferQty <= 0) return;
        
        // Apply transfer: subtract from source, add to destination
        if (String(item.sourceWarehouse) === warehouseIdStr) {
          quantity -= transferQty;
          console.log(`Subtracted ${transferQty} from transfer source ${item.id}`);
        } 
        else if (String(item.destinationWarehouse) === warehouseIdStr) {
          quantity += transferQty;
          console.log(`Added ${transferQty} to transfer destination ${item.id}`);
        }
        return;
      }
      
      // STEP 2.3: Process regular movements (entrée/sortie)
      // Direct product ID match
      if (item.productId === productId) {
        if (item.type === 'entrée' && (String(item.warehouse) === warehouseIdStr || String(item.location) === warehouseIdStr)) {
          quantity += parseInt(item.quantity) || 0;
          console.log(`Added ${item.quantity} from entrée movement ${item.id}`);
        } 
        else if (item.type === 'sortie' && (String(item.warehouse) === warehouseIdStr || String(item.location) === warehouseIdStr)) {
          quantity -= parseInt(item.quantity) || 0;
          console.log(`Subtracted ${item.quantity} from sortie movement ${item.id}`);
        }
      }
      // Items array with matching product ID
      else if (item.items && Array.isArray(item.items)) {
        const matchingItem = item.items.find(i => i.id === productId);
        if (matchingItem) {
          const itemQty = parseInt(matchingItem.quantity) || 0;
          
          if (item.type === 'entrée' && (String(item.warehouse) === warehouseIdStr || String(item.location) === warehouseIdStr)) {
            quantity += itemQty;
            console.log(`Added ${itemQty} from entrée batch movement ${item.id}`);
          } else if (item.type === 'sortie' && (String(item.warehouse) === warehouseIdStr || String(item.location) === warehouseIdStr)) {
            quantity -= itemQty;
            console.log(`Subtracted ${itemQty} from sortie batch movement ${item.id}`);
          }
        }
      }
    });
    
    console.log(`Final calculated quantity for product ${productId} in warehouse ${warehouseIdStr}: ${quantity}`);
    return Math.max(0, quantity); // Don't allow negative stock
  }, [warehouseId, products, stockItems]);

  // Get warehouse name by ID - from real-time warehouse data
  const getWarehouseName = useCallback((warehouseId) => {
    if (!warehouseId) return 'Non spécifié';
    // Always use the latest warehouse data from context
    const found = warehouses?.find(w => String(w.id) === String(warehouseId));
    return found ? found.name : `Entrepôt #${warehouseId}`;
  }, [warehouses]);

  // Get all movements for a specific product - using ONLY real-time data with strict validation
  const getProductMovements = useCallback((productId) => {
    if (!productId) return [];
    
    const movements = [];
    
    // Check stock movements first (direct stock adjustments) - only use movements with valid warehouse IDs
    if (stockItems && Array.isArray(stockItems)) {
      const productStockMovements = stockItems.filter(item => 
        (item.productId === productId || (item.items && item.items.some(i => i.id === productId))) &&
        // Make sure warehouse is specified and is a valid string/number
        item.warehouse !== undefined && 
        item.warehouse !== null
      );
      
      productStockMovements.forEach(movement => {
        // Handle both direct stock movements and items within a movement
        if (movement.productId === productId) {
          // Only create the movement if it has a valid date, type and quantity
          if (movement.date && movement.type && movement.quantity) {
          movements.push({
              date: movement.date,
              type: movement.type,
            quantity: parseInt(movement.quantity) || 0,
              warehouse: movement.warehouse,
              sourceWarehouse: movement.sourceWarehouse || movement.warehouse,
              destinationWarehouse: movement.destinationWarehouse || movement.warehouse,
            reference: movement.reference || 'Mouvement de stock'
          });
          }
        } else if (movement.items) {
          // Find the specific item within the movement
          const item = movement.items.find(i => i.id === productId);
          if (item && movement.date && movement.type && item.quantity) {
            movements.push({
              date: movement.date,
              type: movement.type,
              quantity: parseInt(item.quantity) || 0,
              warehouse: movement.sourceWarehouse || movement.warehouse,
              sourceWarehouse: movement.sourceWarehouse || movement.warehouse,
              destinationWarehouse: movement.destinationWarehouse || movement.location || movement.warehouse,
              reference: movement.reference || 'Mouvement de stock groupé'
            });
          }
        }
      });
    }
    
    // Check purchases for this product to create "entrée" movements - only use purchases with valid warehouse IDs
    if (purchases && Array.isArray(purchases)) {
      const filteredPurchases = purchases.filter(purchase => 
        purchase.warehouseId !== undefined && 
        purchase.warehouseId !== null &&
        purchase.date
      );
      
      filteredPurchases.forEach(purchase => {
        if (purchase.items && Array.isArray(purchase.items)) {
          const purchaseItem = purchase.items.find(item => item.id === productId);
          if (purchaseItem && purchaseItem.quantity) {
            movements.push({
              date: purchase.date,
              type: 'entrée',
              quantity: parseInt(purchaseItem.quantity) || 0,
              warehouse: purchase.warehouseId,
              reference: `Achat #${purchase.purchaseNumber || purchase.id}`
            });
          }
        }
      });
    }
    
    // Check sales for this product to create "sortie" movements - only use sales with valid warehouse IDs
    if (sales && Array.isArray(sales)) {
      const filteredSales = sales.filter(sale => 
        sale.warehouseId !== undefined && 
        sale.warehouseId !== null &&
        sale.date
      );
      
      filteredSales.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          const saleItem = sale.items.find(item => item.id === productId);
          if (saleItem && saleItem.quantity) {
            movements.push({
              date: sale.date,
              type: 'sortie',
              quantity: parseInt(saleItem.quantity) || 0,
              warehouse: sale.warehouseId,
              reference: `Vente #${sale.invoiceNumber || sale.id}`
            });
          }
        }
      });
    }
    
    // Filter out any invalid movements
    const validMovements = movements.filter(movement => 
      movement.date && 
      movement.type && 
      movement.warehouse && 
      movement.quantity !== undefined
    );
    
    // Sort by date, most recent first
    validMovements.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    console.log(`Found ${validMovements.length} valid movements for product ${productId}`);
    return validMovements;
  }, [stockItems, purchases, sales]);
  
  // Get sales history for a specific product - using ONLY real-time sales data with strict validation
  const getProductSales = useCallback((productId) => {
    if (!productId) return [];
    
    const productSales = [];
    
    // Make sure sales data exists and is an array
    if (sales && Array.isArray(sales)) {
      // Filter to only include sales with valid properties
      const validSales = sales.filter(sale => 
        sale.id && 
        sale.date && 
        sale.warehouseId !== undefined && 
        sale.warehouseId !== null &&
        sale.items && 
        Array.isArray(sale.items)
      );
      
      validSales.forEach(sale => {
          const saleItem = sale.items.find(item => item.id === productId);
        if (saleItem && saleItem.quantity && saleItem.price) {
          // Get real data for each sale - no fallbacks to default values
            productSales.push({
              id: sale.id,
            date: sale.date,
              invoiceNumber: sale.invoiceNumber,
            customerName: sale.customerName || 'Client non spécifié',
              quantity: parseInt(saleItem.quantity) || 0,
              price: parseFloat(saleItem.price) || 0,
              warehouseId: sale.warehouseId
            });
        }
      });
    }
    
    // Sort by date, most recent first
    productSales.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    console.log(`Found ${productSales.length} valid sales for product ${productId}`);
    return productSales;
  }, [sales]);
  
  // Get purchase history for a specific product - using ONLY real-time purchase data with strict validation
  const getProductPurchases = useCallback((productId) => {
    if (!productId) return [];
    
    const productPurchases = [];
    
    // Make sure purchases data exists and is an array
    if (purchases && Array.isArray(purchases)) {
      // Filter to only include purchases with valid properties
      const validPurchases = purchases.filter(purchase => 
        purchase.id && 
        purchase.date && 
        purchase.warehouseId !== undefined && 
        purchase.warehouseId !== null &&
        purchase.items && 
        Array.isArray(purchase.items)
      );
      
      validPurchases.forEach(purchase => {
          const purchaseItem = purchase.items.find(item => item.id === productId);
        if (purchaseItem && purchaseItem.quantity) {
            // Find supplier name from real-time data
            let supplierName = purchase.supplierName;
            if (!supplierName && purchase.supplierId && suppliers && Array.isArray(suppliers)) {
              const supplier = suppliers.find(s => String(s.id) === String(purchase.supplierId));
              if (supplier) supplierName = supplier.name;
            }
            
          // Create purchase record with real data - no fallbacks for critical fields
            productPurchases.push({
              id: purchase.id,
            date: purchase.date,
              purchaseNumber: purchase.purchaseNumber,
              supplierName: supplierName || 'Fournisseur non spécifié',
              quantity: parseInt(purchaseItem.quantity) || 0,
              price: parseFloat(purchaseItem.price) || 0,
              warehouseId: purchase.warehouseId,
              notes: purchase.notes || ''
            });
        }
      });
    }
    
    // Sort by date, most recent first
    productPurchases.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    console.log(`Found ${productPurchases.length} valid purchases for product ${productId}`);
    return productPurchases;
  }, [purchases, suppliers]);

  // Get last movement date for a product in this warehouse
  const getLastMovementDate = useCallback((productId) => {
    const movements = getProductMovements(productId);
    if (movements.length === 0) return null;
    
    const warehouseMovements = movements.filter(m => String(m.warehouse) === String(warehouseId));
    if (warehouseMovements.length === 0) return null;
    
    return warehouseMovements[0].date;
  }, [getProductMovements, warehouseId]);

  // Get all products in this warehouse - using real-time product data only
  const warehouseProducts = useMemo(() => {
    if (!products || products.length === 0) {
      console.log("No products available in data context");
      return [];
    }
    
    // Filter to only products in this warehouse (using real-time data)
    const productsInWarehouse = products
      .filter(product => {
        const isInWarehouse = isProductInWarehouse(product);
        console.log(`Product ${product.id} (${product.name}) in warehouse ${warehouseId}? ${isInWarehouse}`);
        return isInWarehouse;
      })
      .map(product => {
        // For each product, calculate all real-time values
        const quantity = getProductQuantityInWarehouse(product.id);
        const lastMove = getLastMovementDate(product.id);
        const productValue = product.price * quantity || 0;
        
        console.log(`Product ${product.id} (${product.name}) quantity in warehouse: ${quantity}`);
        
        return {
          ...product,
          quantityInWarehouse: quantity,
          lastMovementDate: lastMove,
          value: productValue,
          // Add movement counts for quick reference
          movementCount: getProductMovements(product.id).length,
          purchaseCount: getProductPurchases(product.id).length,
          salesCount: getProductSales(product.id).length
        };
      });
    
    console.log(`Found ${productsInWarehouse.length} products in warehouse ${warehouseId}`);
    return productsInWarehouse;
  }, [
    products, 
    isProductInWarehouse, 
    getProductQuantityInWarehouse, 
    getLastMovementDate,
    getProductMovements,
    getProductPurchases,
    getProductSales,
    warehouseId
  ]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    if (!warehouseProducts) return [];
    
    // Filter by search term
    let filtered = warehouseProducts;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name?.toLowerCase().includes(term) ||
        product.sku?.toLowerCase().includes(term) ||
        product.description?.toLowerCase().includes(term) ||
        product.category?.toLowerCase().includes(term)
      );
    }
    
    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(product => product.category === filterCategory);
    }
    
    // Sort products
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (sortBy === 'name' || sortBy === 'category' || sortBy === 'sku') {
        // String comparison
        if (sortDirection === 'asc') {
          return (aValue || '').localeCompare(bValue || '');
        } else {
          return (bValue || '').localeCompare(aValue || '');
        }
      } else {
        // Numeric comparison
        if (sortDirection === 'asc') {
          return (parseFloat(aValue) || 0) - (parseFloat(bValue) || 0);
        } else {
          return (parseFloat(bValue) || 0) - (parseFloat(aValue) || 0);
        }
      }
    });
    
    return sorted;
  }, [warehouseProducts, searchTerm, filterCategory, sortBy, sortDirection]);

  // Toggle sort direction or change sort column
  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0 DA';
    return `${parseInt(amount).toLocaleString('fr-FR')} DA`;
  };

  // Toggle expanded product details
  const toggleExpandProduct = (productId) => {
    if (expandedProduct === productId) {
      setExpandedProduct(null);
    } else {
      setExpandedProduct(productId);
    }
  };

  // Export inventory to CSV
  const exportToCSV = () => {
    try {
      // Format data
      const headers = ['Nom', 'SKU', 'Catégorie', 'Quantité', 'Prix unitaire', 'Valeur totale', 'Dernier mouvement'];
      const rows = filteredProducts.map(product => [
        product.name || 'Sans nom',
        product.sku || 'N/A',
        product.category || 'Non catégorisé',
        product.quantityInWarehouse,
        product.price || 0,
        product.value || 0,
        formatDate(product.lastMovementDate)
      ]);
      
      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      csvContent += rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      
      // Create a blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `inventaire_${warehouse?.name || warehouseId}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Exportation réussie');
    } catch (error) {
      console.error('Error exporting inventory:', error);
      toast.error('Erreur lors de l\'exportation');
    }
  };

  // Load data and keep it refreshed
  useEffect(() => {
    const loadData = async () => {
      // Force direct refresh from localStorage first for instant updates
      try {
        // 1. Get fresh product data directly from localStorage
        const productsData = localStorage.getItem('stockpro_products');
        if (productsData) {
          const freshProducts = JSON.parse(productsData);
          // We don't set products directly here to avoid React state issues
          console.log(`Directly loaded ${freshProducts.length} products from localStorage`);
        }
        
        // 2. Get fresh stock movement data
        const stockData = localStorage.getItem('stockpro_stock');
        if (stockData) {
          // We don't set stockItems directly here to avoid React state issues
          console.log(`Directly loaded stock movements from localStorage`);
        }
      } catch (e) {
        console.error('Error accessing localStorage directly:', e);
      }
      
      // Now use context functions to refresh all data properly
      await getProducts();
      setIsLoading(false);
      
      // Force a refresh of all app data via context
      try {
        // This should be available in scope from AppDataContext
        if (typeof window.refreshAppData === 'function') {
          await window.refreshAppData();
          console.log('Refreshed app data via global function');
        }
      } catch (e) {
        console.error('Error refreshing app data:', e);
      }
      
      console.log("Warehouse inventory data refreshed", {
        products: products?.length || 0,
        sales: sales?.length || 0,
        purchases: purchases?.length || 0,
        stockItems: stockItems?.length || 0
      });
      
      // Run debug analysis after data is loaded
      setTimeout(() => {
        debugWarehouseData();
      }, 500); // Small delay to ensure state is updated
    };
    
    loadData();
    
    // Set up periodic refresh of data (every 30 seconds)
    const refreshInterval = setInterval(() => {
      loadData();
    }, 30000);
    
    // Listen for stock update events
    const handleStockUpdate = (event) => {
      const detail = event.detail || {};
      console.log(`Warehouse inventory (${warehouseId}) received ${event.type} event:`, detail);
      
      // For transfers, do a more thorough check if this warehouse is involved
      const isTransferEvent = event.type === 'transfer-completed';
      const isSourceWarehouse = String(warehouseId) === String(detail.sourceWarehouse);
      const isDestWarehouse = String(warehouseId) === String(detail.destinationWarehouse);
      
      // For refresh-data events, always reload
      const isRefreshEvent = event.type === 'refresh-data';
      
      // Check if this event is relevant for the current warehouse
      const isRelevantWarehouse = 
        isRefreshEvent ||
        String(warehouseId) === String(detail.warehouseId) || 
        isSourceWarehouse ||
        isDestWarehouse;
      
      console.log(`Is this event relevant for current warehouse (${warehouseId})? ${isRelevantWarehouse}`);
      
      if (!isRelevantWarehouse) {
        console.log('Event not relevant to this warehouse, skipping update');
        return;
      }
      
      // Force a complete data reload to ensure we have fresh data
      console.log('Event IS relevant to this warehouse - forcing complete refresh');
      
      // Force refresh trigger to update UI immediately
      setRefreshTrigger(prev => prev + 1);
      
      // Force direct refresh from localStorage first for instant updates
      try {
        // Get fresh product data directly from localStorage
        const productsData = localStorage.getItem('stockpro_products');
        if (productsData) {
          const freshProducts = JSON.parse(productsData);
          console.log('Loaded fresh product data from localStorage:', freshProducts.length);
        }
      } catch (error) {
        console.error('Error forcing localStorage refresh:', error);
      }
      
      // Reload warehouse-specific data with fresh context data
      loadData();
      
      // Run debug analysis after a short delay to allow state to update
      setTimeout(() => {
        debugWarehouseData();
        
        // If this is a transfer event, log details about it
        if (isTransferEvent) {
          console.group('Transfer Details:');
          console.log(`Source warehouse: ${detail.sourceWarehouse}`);
          console.log(`Destination warehouse: ${detail.destinationWarehouse}`);
          console.log('Event details:', detail);
          console.groupEnd();
        }
      }, 500);
    };
    
    // Add event listeners for stock updates, transfers, and custom warehouse updates
    window.addEventListener('product-stock-updated', handleStockUpdate);
    window.addEventListener('transfer-completed', handleStockUpdate);
    window.addEventListener('warehouse-inventory-updated', handleStockUpdate);
    window.addEventListener('refresh-data', handleStockUpdate);
    
    // Clean up interval and event listeners on unmount
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('product-stock-updated', handleStockUpdate);
      window.removeEventListener('transfer-completed', handleStockUpdate);
      window.removeEventListener('warehouse-inventory-updated', handleStockUpdate);
      window.removeEventListener('refresh-data', handleStockUpdate);
    };
  }, [getProducts, refreshTrigger, warehouseId]);
  
  // Debug function to analyze warehouse data
  const debugWarehouseData = useCallback(() => {
    console.group('Warehouse Inventory Debug');
    console.log(`Current warehouse: ${warehouseId} (${warehouse?.name || 'Unknown'})`);
    console.log(`Total products in system: ${products.length}`);
    
    // Count products with this warehouse in their warehouseIds
    const productsWithWarehouseId = products.filter(p => 
      p.warehouseIds && Array.isArray(p.warehouseIds) && 
      p.warehouseIds.some(wId => String(wId) === String(warehouseId))
    );
    console.log(`Products with this warehouse in warehouseIds: ${productsWithWarehouseId.length}`);
    productsWithWarehouseId.forEach(p => {
      console.log(`- Product ${p.id} (${p.name}) has warehouse ${warehouseId} in warehouseIds: ${p.warehouseIds.join(', ')}`);
    });
    
    // Count products with stock in this warehouse
    const productsWithStock = products.filter(p => 
      p.warehouseStock && 
      p.warehouseStock[String(warehouseId)] && 
      p.warehouseStock[String(warehouseId)] > 0
    );
    console.log(`Products with stock in this warehouse: ${productsWithStock.length}`);
    productsWithStock.forEach(p => {
      console.log(`- Product ${p.id} (${p.name}) has ${p.warehouseStock[String(warehouseId)]} units in warehouseStock`);
    });
    
    // Check stock movements for this warehouse
    const warehouseMovements = stockItems.filter(item => 
      String(item.warehouse) === String(warehouseId) || 
      String(item.location) === String(warehouseId) ||
      String(item.sourceWarehouse) === String(warehouseId) ||
      String(item.destinationWarehouse) === String(warehouseId)
    );
    console.log(`Stock movements for this warehouse: ${warehouseMovements.length}`);
    warehouseMovements.slice(0, 5).forEach(m => {
      console.log(`- Movement ${m.id}: ${m.type}, Product: ${m.productId}, Quantity: ${m.quantity}`);
      console.log(`  Warehouse IDs: warehouse=${m.warehouse}, location=${m.location}, source=${m.sourceWarehouse}, dest=${m.destinationWarehouse}`);
    });
    
    // Check purchases for this warehouse
    const warehousePurchases = purchases.filter(p => String(p.warehouseId) === String(warehouseId));
    console.log(`Purchases for this warehouse: ${warehousePurchases.length}`);
    
    // Log all products that should be in this warehouse
    const productsInWarehouse = products.filter(p => isProductInWarehouse(p));
    console.log(`Total products determined to be in this warehouse: ${productsInWarehouse.length}`);
    productsInWarehouse.forEach(p => {
      const quantity = getProductQuantityInWarehouse(p.id);
      console.log(`- ${p.name}: ${quantity} units (ID: ${p.id})`);
      
      // Detailed analysis of quantity calculation
      console.group(`Detailed quantity analysis for ${p.name} (${p.id})`);
      
      // Check warehouseStock
      if (p.warehouseStock && p.warehouseStock[String(warehouseId)] !== undefined) {
        console.log(`warehouseStock: ${p.warehouseStock[String(warehouseId)]}`);
      } else {
        console.log('No warehouseStock entry for this warehouse');
      }
      
      // Check movements
      const productMovements = stockItems.filter(item => 
        (item.productId === p.id || (item.items && item.items.some(i => i.id === p.id)))
      );
      
      console.log(`Found ${productMovements.length} movements for this product`);
      
      // Analyze each movement
      let calculatedQuantity = 0;
      productMovements.forEach(item => {
        const isSourceWarehouse = String(item.sourceWarehouse) === String(warehouseId);
        const isDestWarehouse = String(item.destinationWarehouse) === String(warehouseId);
        const isWarehouse = String(item.warehouse) === String(warehouseId);
        const isLocation = String(item.location) === String(warehouseId);
        
        // COMPLETELY DIFFERENT APPROACH: Only use warehouseStock for transfers
        // First, check if this product has warehouseStock data
        if (p.warehouseStock && p.warehouseStock[String(warehouseId)] !== undefined) {
          console.log(`Using warehouseStock for ${p.name} in warehouse ${warehouseId}: ${p.warehouseStock[String(warehouseId)]}`);
          calculatedQuantity = parseInt(p.warehouseStock[String(warehouseId)]) || 0;
          console.log(`Skipping movement processing, using warehouseStock directly: ${calculatedQuantity}`);
          // Don't process any more movements for this product
          return;
        }
        
        // For non-transfer products, process movements as usual
        // But completely ignore any transfer-related movements
        const isTransfer = item.type === 'transfert';
        const isTransferPart = item.transferId || item.isTransfer;
        const isTransferRelated = isTransfer || isTransferPart;
        
        // Skip all transfer-related movements
        if (isTransferRelated) {
          console.log(`Skipping transfer-related movement: ${item.id}`);
          // Skip to next iteration without using continue
          return;
        }
        
        // Process regular movements only
        let shouldCount = false;
        let qtyToUse = 0;
        
        if (isWarehouse || isLocation) {
          // Direct product ID match
          if (item.productId === p.id) {
            shouldCount = true;
            qtyToUse = parseInt(item.quantity) || 0;
            console.log(`Regular movement, direct match: ${item.id}, Quantity: ${qtyToUse}`);
          }
          // Items array with matching product ID
          else if (item.items && Array.isArray(item.items)) {
            const matchingSubItem = item.items.find(i => i.id === p.id);
            if (matchingSubItem) {
              shouldCount = true;
              qtyToUse = parseInt(matchingSubItem.quantity) || 0;
              console.log(`Regular movement, items array match: ${item.id}, Quantity: ${qtyToUse}`);
            }
          }
          
          if (shouldCount && qtyToUse > 0) {
            console.log(`  Warehouse: ${item.warehouse}, Location: ${item.location}`);
            
            if (item.type === 'entrée') {
              calculatedQuantity += qtyToUse;
              console.log(`  Added ${qtyToUse} from entry (new total: ${calculatedQuantity})`);
            } else if (item.type === 'sortie') {
              calculatedQuantity -= qtyToUse;
              console.log(`  Subtracted ${qtyToUse} from exit (new total: ${calculatedQuantity})`);
            }
          }
        }
      });
      
      console.log(`Final calculated quantity: ${Math.max(0, calculatedQuantity)}`);
      console.groupEnd();
    });
    
    // Check the warehouse data in the warehouses array
    const warehouseData = warehouses.find(w => String(w.id) === String(warehouseId));
    console.log('Warehouse data from warehouses array:', warehouseData);
    
    // Check if warehouse IDs in the UI match warehouse IDs in the data
    console.log(`Warehouse ID in URL: ${warehouseId} (${typeof warehouseId})`);
    console.log(`Warehouse IDs in warehouses array: ${warehouses.map(w => `${w.id} (${typeof w.id})`).join(', ')}`);
    
    console.groupEnd();
  }, [warehouseId, warehouse, products, stockItems, purchases, warehouses, isProductInWarehouse, getProductQuantityInWarehouse]);
  
  // Reset active tab when expanding a different product
  useEffect(() => {
    if (expandedProduct !== null) {
      setActiveTab('details');
    }
  }, [expandedProduct]);

  // Calculate totals
  const totalItems = filteredProducts.reduce((sum, product) => sum + (product.quantityInWarehouse || 0), 0);
  const totalValue = filteredProducts.reduce((sum, product) => sum + (product.value || 0), 0);
  const totalProducts = filteredProducts.length;

  // If warehouse not found
  if (!isLoading && !warehouse) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <BuildingStorefrontIcon className="h-16 w-16 text-white/20 mb-4" />
        <h3 className="text-xl font-semibold text-white">Entrepôt non trouvé</h3>
        <p className="text-white/60 mt-2">L'entrepôt demandé n'existe pas ou a été supprimé</p>
        <button
          onClick={() => navigate('/warehouses')}
          className="mt-6 flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour aux entrepôts
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/warehouses')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center">
              <BuildingStorefrontIcon className="h-8 w-8 mr-3 text-blue-400" />
              {warehouse?.name || 'Inventaire de l\'entrepôt'}
            </h1>
            <p className="text-white/60 mt-1">
              {warehouse?.location && (
                <span className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {warehouse.location}
                </span>
              )}
            </p>
          </div>
        </div>
        
                <div className="flex space-x-2">
          {/* Debug button */}
          <button 
            onClick={() => {
              toast.success('Analyse des données...');
              debugWarehouseData();
            }}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            title="Analyser les données"
          >
            <InformationCircleIcon className="h-6 w-6 text-blue-400" />
          </button>
          
          {/* Refresh button */}
          <button
            onClick={() => {
              toast.success('Actualisation des données...');
              getProducts();
              setRefreshTrigger(prev => prev + 1);
            }}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            title="Actualiser les données"
          >
            <ArrowPathIcon className="h-6 w-6 text-white" />
          </button>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {/* Total Products */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60">Nombre de produits</p>
              <p className="text-3xl font-bold text-white mt-1">{totalProducts}</p>
            </div>
            <div className="p-3 bg-blue-500/20 rounded-full">
              <ArchiveBoxIcon className="h-7 w-7 text-blue-400" />
            </div>
          </div>
        </div>
        
        {/* Total Items */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60">Quantité totale</p>
              <p className="text-3xl font-bold text-white mt-1">{totalItems}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-full">
              <ArchiveBoxIcon className="h-7 w-7 text-green-400" />
            </div>
          </div>
        </div>
        
        {/* Total Value */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60">Valeur du stock</p>
              <p className="text-3xl font-bold text-white mt-1">{formatCurrency(totalValue)}</p>
            </div>
            <div className="p-3 bg-red-500/20 rounded-full">
              <CurrencyDollarIcon className="h-7 w-7 text-red-400" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-grow md:flex-grow-0 md:min-w-[300px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-xl pl-10 pr-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-white/40 absolute left-3 top-2.5" />
            </div>
          </div>
          
          {/* Category Filter */}
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-white/10 border border-white/20 text-white rounded-xl py-2 pl-4 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes catégories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          {/* Reset Filters */}
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterCategory('all');
              setSortBy('name');
              setSortDirection('asc');
            }}
            className="flex items-center px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Réinitialiser
          </button>
        </div>
      </div>
      
      {/* Products Table */}
      {isLoading ? (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 animate-pulse">
          <div className="h-8 bg-white/20 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-white/10 rounded"></div>
            <div className="h-12 bg-white/10 rounded"></div>
            <div className="h-12 bg-white/10 rounded"></div>
          </div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 text-center py-12">
          <ArchiveBoxIcon className="h-16 w-16 mx-auto text-white/20 mb-4" />
          <h3 className="text-xl font-semibold text-white">Aucun produit trouvé</h3>
          <p className="text-white/60 mt-2">
            {searchTerm || filterCategory !== 'all' 
              ? 'Aucun produit ne correspond à vos critères de recherche' 
              : 'Cet entrepôt ne contient aucun produit'}
          </p>
        </div>
      ) : (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 overflow-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th 
                  className="py-3 text-left cursor-pointer"
                  onClick={() => toggleSort('name')}
                >
                  <div className="flex items-center text-white/60 hover:text-white">
                    Produit
                    {sortBy === 'name' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-left cursor-pointer"
                  onClick={() => toggleSort('sku')}
                >
                  <div className="flex items-center text-white/60 hover:text-white">
                    SKU
                    {sortBy === 'sku' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-left cursor-pointer"
                  onClick={() => toggleSort('category')}
                >
                  <div className="flex items-center text-white/60 hover:text-white">
                    Catégorie
                    {sortBy === 'category' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-right cursor-pointer"
                  onClick={() => toggleSort('quantityInWarehouse')}
                >
                  <div className="flex items-center justify-end text-white/60 hover:text-white">
                    Quantité
                    {sortBy === 'quantityInWarehouse' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-right cursor-pointer"
                  onClick={() => toggleSort('price')}
                >
                  <div className="flex items-center justify-end text-white/60 hover:text-white">
                    Prix unitaire
                    {sortBy === 'price' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-right cursor-pointer"
                  onClick={() => toggleSort('value')}
                >
                  <div className="flex items-center justify-end text-white/60 hover:text-white">
                    Valeur
                    {sortBy === 'value' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="py-3 text-right cursor-pointer"
                  onClick={() => toggleSort('lastMovementDate')}
                >
                  <div className="flex items-center justify-end text-white/60 hover:text-white">
                    Dernier mouvement
                    {sortBy === 'lastMovementDate' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="h-4 w-4 ml-1" /> 
                        : <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 text-center">
                  <div className="text-white/60">
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredProducts.map(product => (
                <React.Fragment key={product.id}>
                  <tr className="hover:bg-white/5">
                    <td className="py-4 text-white font-medium">{product.name}</td>
                    <td className="py-4 text-white/80">{product.sku || 'N/A'}</td>
                    <td className="py-4 text-white/80">{product.category || 'Non catégorisé'}</td>
                    <td className="py-4 text-white/80 text-right">
                      <span className={`${product.quantityInWarehouse < 5 ? 'text-red-400' : 'text-green-400'}`}>
                        {product.quantityInWarehouse}
                      </span>
                    </td>
                    <td className="py-4 text-white/80 text-right">{formatCurrency(product.price)}</td>
                    <td className="py-4 text-white/80 text-right">{formatCurrency(product.value)}</td>
                    <td className="py-4 text-white/80 text-right">
                      <span className="flex items-center justify-end">
                        <CalendarDaysIcon className="h-4 w-4 mr-1" />
                        {formatDate(product.lastMovementDate)}
                      </span>
                    </td>
                    <td className="py-4 text-center">
                      <div className="flex space-x-2 justify-center">
                        <button
                          onClick={() => {
                            setExpandedProduct(product.id);
                            setActiveTab('movements');
                          }}
                          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                          title="Mouvements"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setExpandedProduct(product.id);
                            setActiveTab('sales');
                          }}
                          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                          title="Ventes"
                        >
                          <ShoppingCartIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setExpandedProduct(product.id);
                            setActiveTab('purchases');
                          }}
                          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                          title="Achats"
                        >
                          <TruckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setExpandedProduct(product.id);
                            setActiveTab('details');
                          }}
                          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                          title="Détails"
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded product details */}
                  {expandedProduct === product.id && (
                    <tr>
                      <td colSpan={8} className="bg-black/20 p-4 rounded-lg">
                        {/* Tabs for different product views */}
                        <div className="flex border-b border-white/10 mb-4">
                          <button
                            className={`px-4 py-2 ${activeTab === 'details' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-white/60 hover:text-white'}`}
                            onClick={() => setActiveTab('details')}
                          >
                            <div className="flex items-center">
                              <InformationCircleIcon className="h-4 w-4 mr-2" />
                              Détails
                            </div>
                          </button>
                          <button
                            className={`px-4 py-2 ${activeTab === 'movements' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-white/60 hover:text-white'}`}
                            onClick={() => setActiveTab('movements')}
                          >
                            <div className="flex items-center">
                              <ArrowPathIcon className="h-4 w-4 mr-2" />
                              Mouvements
                            </div>
                          </button>
                          <button
                            className={`px-4 py-2 ${activeTab === 'sales' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-white/60 hover:text-white'}`}
                            onClick={() => setActiveTab('sales')}
                          >
                            <div className="flex items-center">
                              <ShoppingCartIcon className="h-4 w-4 mr-2" />
                              Ventes
                            </div>
                          </button>
                          <button
                            className={`px-4 py-2 ${activeTab === 'purchases' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-white/60 hover:text-white'}`}
                            onClick={() => setActiveTab('purchases')}
                          >
                            <div className="flex items-center">
                              <TruckIcon className="h-4 w-4 mr-2" />
                              Achats
                            </div>
                          </button>
                        </div>
                        
                        {/* Tab Content */}
                        {activeTab === 'details' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-white font-semibold mb-2 flex items-center">
                                <TagIcon className="h-4 w-4 mr-2 text-blue-400" />
                                Détails du produit
                              </h4>
                              <div className="space-y-2">
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Description:</span> {product.description || 'Aucune description'}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Fournisseur:</span> {product.supplier || 'Non spécifié'}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">SKU:</span> {product.sku || 'Non spécifié'}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Dimensions:</span> {product.dimensions || 'Non spécifiées'}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Poids:</span> {product.weight ? `${product.weight} kg` : 'Non spécifié'}
                                </p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-white font-semibold mb-2 flex items-center">
                                <ArchiveBoxIcon className="h-4 w-4 mr-2 text-blue-400" />
                                Stock et mouvements
                              </h4>
                              <div className="space-y-2">
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Stock minimum:</span> {product.minStock || 0}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Stock optimal:</span> {product.optimalStock || 'Non défini'}
                                </p>
                                <p className="text-white/80 text-sm">
                                  <span className="text-white/60">Emplacement:</span> {product.location || 'Non spécifié'}
                                </p>
                                <p className={`text-sm ${product.quantityInWarehouse < product.minStock ? 'text-red-400' : 'text-green-400'}`}>
                                  <span className="text-white/60">Statut:</span> {
                                    product.quantityInWarehouse <= 0 ? 'Rupture de stock' :
                                    product.quantityInWarehouse < product.minStock ? 'Stock bas' :
                                    'En stock'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {activeTab === 'movements' && (
                          <div>
                            <h4 className="text-white font-semibold mb-3 flex items-center">
                              <ArrowPathIcon className="h-4 w-4 mr-2 text-blue-400" />
                              Historique des mouvements
                            </h4>
                            
                            {getProductMovements(product.id)
                              .filter(movement => String(movement.warehouse) === String(warehouseId))
                              .length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full">
                                  <thead>
                                    <tr className="border-b border-white/10">
                                      <th className="py-2 text-left text-white/60 text-xs">Date</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Type</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Quantité</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Entrepôt</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Référence</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {getProductMovements(product.id)
                                      .filter(movement => String(movement.warehouse) === String(warehouseId))
                                      .map((movement, idx) => (
                                      <tr key={`movement-${product.id}-${idx}`}>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatDate(movement.date)}
                                        </td>
                                        <td className="py-2 text-xs">
                                          <span className={`px-2 py-1 rounded-full ${
                                            movement.type === 'entrée' ? 'bg-green-500/20 text-green-400' : 
                                            movement.type === 'sortie' ? 'bg-red-500/20 text-red-400' :
                                            'bg-blue-500/20 text-blue-400'
                                          }`}>
                                            {movement.type === 'entrée' ? 'Entrée' : 
                                             movement.type === 'sortie' ? 'Sortie' : 
                                             'Transfert'}
                                          </span>
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {movement.quantity}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {movement.type === 'transfert' ? (
                                            <div className="flex items-center">
                                              <span>{getWarehouseName(movement.sourceWarehouse || movement.warehouse)}</span>
                                              <ArrowRightIcon className="h-3 w-3 mx-1 text-blue-400" />
                                              <span>{getWarehouseName(movement.destinationWarehouse || movement.warehouse)}</span>
                                            </div>
                                          ) : (
                                            getWarehouseName(movement.warehouse)
                                          )}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {movement.reference || 'N/A'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-white/60 text-sm">Aucun mouvement enregistré pour ce produit dans cet entrepôt</p>
                            )}
                          </div>
                        )}
                        
                        {activeTab === 'sales' && (
                          <div>
                            <h4 className="text-white font-semibold mb-3 flex items-center">
                              <ShoppingCartIcon className="h-4 w-4 mr-2 text-blue-400" />
                              Historique des ventes
                            </h4>
                            
                            {getProductSales(product.id)
                              .filter(sale => String(sale.warehouseId) === String(warehouseId))
                              .length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full">
                                  <thead>
                                    <tr className="border-b border-white/10">
                                      <th className="py-2 text-left text-white/60 text-xs">Date</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Facture</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Client</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Quantité</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Prix unitaire</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {getProductSales(product.id)
                                      .filter(sale => String(sale.warehouseId) === String(warehouseId))
                                      .map((sale, idx) => (
                                      <tr key={`sale-${product.id}-${idx}`}>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatDate(sale.date)}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {sale.invoiceNumber || sale.id}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {sale.customerName || 'Client non spécifié'}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {sale.quantity}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatCurrency(sale.price)}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatCurrency(sale.price * sale.quantity)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-white/60 text-sm">Aucune vente enregistrée pour ce produit dans cet entrepôt</p>
                            )}
                          </div>
                        )}
                        
                        {activeTab === 'purchases' && (
                          <div>
                            <h4 className="text-white font-semibold mb-3 flex items-center">
                              <TruckIcon className="h-4 w-4 mr-2 text-blue-400" />
                              Historique des achats
                            </h4>
                            
                            {getProductPurchases(product.id)
                              .filter(purchase => String(purchase.warehouseId) === String(warehouseId))
                              .length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full">
                                  <thead>
                                    <tr className="border-b border-white/10">
                                      <th className="py-2 text-left text-white/60 text-xs">Date</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Bon de commande</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Fournisseur</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Quantité</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Prix unitaire</th>
                                      <th className="py-2 text-left text-white/60 text-xs">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {getProductPurchases(product.id)
                                      .filter(purchase => String(purchase.warehouseId) === String(warehouseId))
                                      .map((purchase, idx) => (
                                      <tr key={`purchase-${product.id}-${idx}`}>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatDate(purchase.date)}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {purchase.purchaseNumber || purchase.id}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {purchase.supplierName || 'Fournisseur non spécifié'}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {purchase.quantity}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatCurrency(purchase.price)}
                                        </td>
                                        <td className="py-2 text-white/80 text-xs">
                                          {formatCurrency(purchase.price * purchase.quantity)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-white/60 text-sm">Aucun achat enregistré pour ce produit dans cet entrepôt</p>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WarehouseInventoryPage;